import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SignalCellularAltOutlinedIcon from '@mui/icons-material/SignalCellularAltOutlined';
import { Link, useParams } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import {
  DEFAULT_EBOOK_SLUG,
  buildEbookPage,
  ebookChapterPath,
  formatReadTime,
  KC_HOME_PATH,
} from './repository';
import { KC_COLORS } from './theme';
import { KC_TRUNCATE, truncateAtWord } from './truncateAtWord';
import { NotFound } from '../notFound/NotFound';
import './knowledgeCenter.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const PART_ACCENTS = [
  '#009BAA',
  '#0E8C9B',
  '#1A6F8C',
  '#1F5F8A',
  '#254F7A',
  '#2A406C',
  '#0A1D3A',
];

function EbookBreadcrumb({ breadcrumbs, light = false }) {
  const active = light ? 'rgba(255,255,255,0.92)' : KC_COLORS.navy700;
  const muted = light ? 'rgba(255,255,255,0.55)' : KC_COLORS.gray600;

  return (
    <nav aria-label="Breadcrumb" className="kc-article-breadcrumb mb-5 md:mb-6">
      <ol
        className="flex flex-wrap items-center gap-x-2 gap-y-1 kc-type-body font-semibold"
        style={{ color: active, fontSize: '14px', lineHeight: '20px' }}
      >
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <li key={`${crumb.label}-${index}`} className="inline-flex items-center gap-2">
              {index > 0 ? (
                <span className="font-medium" style={{ color: muted }}>
                  &gt;
                </span>
              ) : null}
              {crumb.path && !isLast ? (
                <Link
                  to={crumb.path}
                  className="transition-opacity hover:opacity-80 no-underline"
                  style={{ color: active }}
                >
                  {crumb.label}
                </Link>
              ) : (
                <span style={{ color: isLast ? muted : active }}>{crumb.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function StatPill({ icon: Icon, label, color = KC_COLORS.teal }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 kc-type-caption font-medium backdrop-blur-sm"
      style={{
        borderColor: 'rgba(255,255,255,0.18)',
        backgroundColor: 'rgba(255,255,255,0.08)',
        color: 'rgba(255,255,255,0.92)',
      }}
    >
      <Icon sx={{ fontSize: 16, color }} />
      {label}
    </span>
  );
}

export function KnowledgeCenterEbook({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.ebookSlug || DEFAULT_EBOOK_SLUG;
  const pageScope = useRef(null);
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading');
  const [activePartId, setActivePartId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setPage(null);

    buildEbookPage(slug).then((nextPage) => {
      if (cancelled) return;
      if (!nextPage) {
        setStatus('missing');
        return;
      }
      setPage(nextPage);
      setActivePartId(nextPage.parts?.[0]?.id || null);
      setStatus('ready');
    });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const partNav = useMemo(
    () =>
      (page?.parts || []).map((part, index) => ({
        ...part,
        accent: PART_ACCENTS[index % PART_ACCENTS.length],
      })),
    [page]
  );

  useGSAP(
    () => {
      if (!page) return undefined;

      const intro = gsap.timeline({ defaults: { ease: 'power2.out' } });
      intro
        .fromTo('.kc-ebook-hero-crumb', { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4 })
        .fromTo(
          '.kc-ebook-hero-title',
          { y: 28, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55 },
          '-=0.15'
        )
        .fromTo(
          '.kc-ebook-hero-copy',
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45 },
          '-=0.25'
        )
        .fromTo(
          '.kc-ebook-hero-meta',
          { y: 14, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4 },
          '-=0.2'
        )
        .fromTo(
          '.kc-ebook-hero-cta',
          { y: 12, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.35 },
          '-=0.15'
        )
        .fromTo(
          '.kc-ebook-hero-cover',
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45 },
          '-=0.35'
        );

      gsap.fromTo(
        '.kc-ebook-intro-panel',
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
          scrollTrigger: { trigger: '.kc-ebook-intro-panel', start: 'top 88%', once: true },
        }
      );

      gsap.fromTo(
        '.kc-ebook-part',
        { y: 26, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.08,
          ease: 'power2.out',
          scrollTrigger: { trigger: '.kc-ebook-toc', start: 'top 85%', once: true },
        }
      );

      const triggers = (page.parts || []).map((part) =>
        ScrollTrigger.create({
          trigger: `#${part.id}`,
          start: 'top 35%',
          end: 'bottom 35%',
          onEnter: () => setActivePartId(part.id),
          onEnterBack: () => setActivePartId(part.id),
        })
      );

      return () => {
        triggers.forEach((t) => t.kill());
      };
    },
    { scope: pageScope, dependencies: [slug, page] }
  );

  const scrollToPart = (partId) => {
    const el = document.getElementById(partId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActivePartId(partId);
  };

  if (status === 'missing') return <NotFound />;

  if (status === 'loading' || !page) {
    return (
      <div className="kc-ds w-full overflow-x-hidden bg-white min-h-screen">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain className="knowledge-center-article-page">
          <Box className="w-full bg-white pb-12 md:pb-16">
            <Container maxWidth="lg">
              <div className="pt-28 md:pt-32 pb-16">
                <p className="kc-type-body" style={{ color: KC_COLORS.gray600 }}>
                  Loading ebook…
                </p>
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <PageSeoHelmet {...page.seo} />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain ref={pageScope} className="knowledge-center-article-page">
          {/* Hero */}
          <section className="kc-ebook-hero relative overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(145deg, #071526 0%, #0A1D3A 52%, #0B3A45 100%)',
              }}
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at 8% 0%, rgba(0,155,170,0.22) 0%, transparent 42%)',
              }}
              aria-hidden
            />

            <Container maxWidth="lg" className="relative">
              <div className="pt-28 md:pt-32 pb-12 md:pb-14 text-left">
                <div className="kc-ebook-hero-crumb">
                  <EbookBreadcrumb breadcrumbs={page.breadcrumbs} light />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                  <div className="lg:col-span-8">
                    <p
                      className="m-0 mb-3 inline-flex items-center gap-2 uppercase tracking-[0.12em] font-bold"
                      style={{ color: '#5EEAD4', fontSize: '12px', lineHeight: '16px' }}
                    >
                      <AutoStoriesOutlinedIcon sx={{ fontSize: 16 }} />
                      Executive Ebook
                    </p>

                    <h1
                      className="kc-ebook-hero-title m-0 break-words"
                      style={{
                        color: KC_COLORS.white,
                        fontSize: 'clamp(28px, 4vw, 44px)',
                        lineHeight: 1.15,
                        fontWeight: 700,
                        letterSpacing: '0.2px',
                        maxWidth: '20ch',
                      }}
                    >
                      {page.h1 || page.title}
                    </h1>

                    {page.subtitle ? (
                      <p
                        className="kc-ebook-hero-copy m-0 mt-4 md:mt-5 max-w-2xl break-words"
                        style={{
                          color: 'rgba(255,255,255,0.72)',
                          fontSize: '16px',
                          lineHeight: '26px',
                        }}
                      >
                        {page.subtitle}
                      </p>
                    ) : null}

                    <div className="kc-ebook-hero-meta mt-5 md:mt-6 flex flex-wrap gap-2">
                      <StatPill
                        icon={MenuBookOutlinedIcon}
                        label={`${page.chapterCount} chapters`}
                      />
                      <StatPill
                        icon={LayersOutlinedIcon}
                        label={`${page.partCount} parts`}
                      />
                      <StatPill icon={AccessTimeOutlinedIcon} label={page.readTime} />
                      <StatPill
                        icon={SignalCellularAltOutlinedIcon}
                        label={page.difficulty}
                        color={KC_COLORS.successGreen}
                      />
                    </div>

                    <div className="kc-ebook-hero-cta mt-7 flex flex-wrap gap-3">
                      {page.firstChapterSlug ? (
                        <Link
                          to={ebookChapterPath(page.slug, page.firstChapterSlug)}
                          className="inline-flex items-center gap-1.5 rounded-xl px-5 py-3 font-semibold no-underline transition-opacity hover:opacity-90"
                          style={{
                            backgroundColor: KC_COLORS.teal,
                            color: KC_COLORS.white,
                            fontSize: '15px',
                            lineHeight: '22px',
                          }}
                        >
                          Start reading
                          <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                        </Link>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => scrollToPart(page.parts?.[0]?.id)}
                        className="inline-flex items-center gap-1.5 rounded-xl px-5 py-3 font-semibold transition-colors"
                        style={{
                          border: '1.5px solid rgba(255,255,255,0.28)',
                          color: KC_COLORS.white,
                          backgroundColor: 'rgba(255,255,255,0.06)',
                          fontSize: '15px',
                          lineHeight: '22px',
                          cursor: 'pointer',
                        }}
                      >
                        Browse table of contents
                      </button>
                    </div>
                  </div>

                  <div className="lg:col-span-4 hidden lg:flex justify-center">
                    <div className="kc-ebook-hero-cover flex flex-col items-center pr-2">
                      <div className="kc-ebook-cover" aria-hidden>
                        <div className="kc-ebook-cover-pages" />
                        <div className="kc-ebook-cover-face">
                          <p
                            className="m-0 uppercase tracking-[0.16em] font-bold"
                            style={{ color: '#5EEAD4', fontSize: '9px', lineHeight: '12px' }}
                          >
                            Charlee
                          </p>
                          <p
                            className="m-0 mt-3 font-semibold"
                            style={{
                              color: KC_COLORS.white,
                              fontSize: '15px',
                              lineHeight: '20px',
                            }}
                          >
                            Executive Guide to Document Intelligence
                          </p>
                          <p
                            className="m-0 mt-auto pt-6"
                            style={{
                              color: 'rgba(255,255,255,0.55)',
                              fontSize: '10px',
                              lineHeight: '14px',
                            }}
                          >
                            Property &amp; Casualty
                          </p>
                        </div>
                      </div>
                      <p
                        className="m-0 mt-5 text-center max-w-[220px]"
                        style={{
                          color: 'rgba(255,255,255,0.62)',
                          fontSize: '13px',
                          lineHeight: '20px',
                        }}
                      >
                        A practical publication for P&amp;C leaders.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Container>
            <div className="kc-ebook-hero-edge relative" aria-hidden />
          </section>

          <Box className="w-full bg-white pb-14 md:pb-20">
            <Container maxWidth="lg">
              {/* Intro */}
              <div
                className="kc-ebook-intro-panel relative overflow-hidden rounded-3xl border px-5 py-5 sm:px-6 sm:py-6 md:px-8 md:py-7 mt-8 md:mt-10"
                style={{
                  borderColor: KC_COLORS.border,
                  backgroundColor: KC_COLORS.white,
                }}
              >
                <span
                  className="absolute left-0 top-0 bottom-0 w-[3px]"
                  style={{ backgroundColor: KC_COLORS.teal }}
                  aria-hidden
                />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                  <div className="lg:col-span-8 text-left">
                    <p
                      className="m-0 mb-3 uppercase tracking-[0.1em] font-bold"
                      style={{ color: KC_COLORS.teal, fontSize: '12px' }}
                    >
                      About this guide
                    </p>
                    {(page.introduction || []).map((para) => (
                      <p
                        key={para.slice(0, 48)}
                        className="kc-type-body m-0 mb-3 last:mb-0"
                        style={{ color: KC_COLORS.navy700 }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                  <div className="lg:col-span-4">
                    <div
                      className="h-full rounded-2xl border px-4 py-4"
                      style={{
                        borderColor: '#D7EEF1',
                        backgroundColor: KC_COLORS.tealLight,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <GroupsOutlinedIcon sx={{ fontSize: 22, color: KC_COLORS.teal, mt: '2px' }} />
                        <div>
                          <p
                            className="m-0 font-semibold"
                            style={{ color: KC_COLORS.primaryNavy, fontSize: '14px' }}
                          >
                            Written for
                          </p>
                          <p
                            className="m-0 mt-1.5"
                            style={{
                              color: KC_COLORS.navy700,
                              fontSize: '13px',
                              lineHeight: '20px',
                            }}
                          >
                            {page.audience}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* TOC */}
              <div className="kc-ebook-toc mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-8 lg:gap-10 items-start">
                <aside className="hidden lg:block sticky top-28 self-start">
                  <p
                    className="m-0 mb-3 uppercase tracking-[0.1em] font-bold"
                    style={{ color: KC_COLORS.gray600, fontSize: '11px' }}
                  >
                    Contents
                  </p>
                  <nav aria-label="Ebook parts" className="flex flex-col gap-1.5">
                    {partNav.map((part) => {
                      const active = activePartId === part.id;
                      return (
                        <button
                          key={part.id}
                          type="button"
                          onClick={() => scrollToPart(part.id)}
                          className="text-left rounded-xl px-3 py-2.5 transition-colors"
                          style={{
                            border: `1px solid ${active ? 'rgba(0,155,170,0.35)' : 'transparent'}`,
                            backgroundColor: active ? 'rgba(0,155,170,0.08)' : 'transparent',
                            cursor: 'pointer',
                          }}
                        >
                          <span
                            className="block font-bold uppercase tracking-[0.08em]"
                            style={{
                              color: active ? KC_COLORS.teal : KC_COLORS.gray600,
                              fontSize: '11px',
                              lineHeight: '14px',
                            }}
                          >
                            Part {part.number}
                          </span>
                          <span
                            className="block mt-1 font-semibold break-words"
                            style={{
                              color: KC_COLORS.primaryNavy,
                              fontSize: '13px',
                              lineHeight: '18px',
                            }}
                          >
                            {part.title}
                          </span>
                        </button>
                      );
                    })}
                  </nav>
                </aside>

                <div className="min-w-0 space-y-8 md:space-y-10 text-left">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <p
                        className="m-0 uppercase tracking-[0.1em] font-bold"
                        style={{ color: KC_COLORS.teal, fontSize: '12px' }}
                      >
                        Table of contents
                      </p>
                      <h2
                        className="m-0 mt-1"
                        style={{
                          color: KC_COLORS.primaryNavy,
                          fontSize: '28px',
                          lineHeight: '36px',
                          fontWeight: 700,
                        }}
                      >
                        Explore all {page.chapterCount} chapters
                      </h2>
                    </div>
                  </div>

                  {/* Mobile part chips */}
                  <div className="lg:hidden -mx-1 overflow-x-auto pb-1">
                    <div className="flex gap-2 px-1 min-w-max">
                      {partNav.map((part) => {
                        const active = activePartId === part.id;
                        return (
                          <button
                            key={part.id}
                            type="button"
                            onClick={() => scrollToPart(part.id)}
                            className="rounded-full px-3.5 py-2 font-semibold whitespace-nowrap"
                            style={{
                              border: `1px solid ${active ? KC_COLORS.teal : KC_COLORS.border}`,
                              backgroundColor: active ? KC_COLORS.teal : KC_COLORS.white,
                              color: active ? KC_COLORS.white : KC_COLORS.primaryNavy,
                              fontSize: '13px',
                              cursor: 'pointer',
                            }}
                          >
                            Part {part.number}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {partNav.map((part) => (
                    <section
                      key={part.id}
                      id={part.id}
                      className="kc-ebook-part scroll-mt-28 rounded-3xl border overflow-hidden"
                      style={{
                        borderColor: KC_COLORS.border,
                        backgroundColor: KC_COLORS.white,
                        boxShadow: '0 10px 30px rgba(10,29,58,0.04)',
                      }}
                    >
                      <div
                        className="px-4 py-4 sm:px-5 sm:py-5 md:px-6 border-b"
                        style={{
                          borderColor: KC_COLORS.border,
                          background:
                            'linear-gradient(135deg, rgba(0,155,170,0.06) 0%, rgba(10,29,58,0.03) 100%)',
                        }}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2.5">
                              <span
                                className="inline-flex h-8 min-w-8 items-center justify-center rounded-lg font-bold"
                                style={{
                                  backgroundColor: part.accent,
                                  color: KC_COLORS.white,
                                  fontSize: '12px',
                                }}
                              >
                                {String(part.number).padStart(2, '0')}
                              </span>
                              <p
                                className="m-0 uppercase tracking-[0.1em] font-bold"
                                style={{ color: KC_COLORS.teal, fontSize: '11px' }}
                              >
                                Part {part.number}
                              </p>
                            </div>
                            <h3
                              className="m-0 mt-2 break-words"
                              style={{
                                color: KC_COLORS.primaryNavy,
                                fontSize: '22px',
                                lineHeight: '30px',
                                fontWeight: 700,
                              }}
                            >
                              {part.title}
                            </h3>
                          </div>
                          <span
                            className="inline-flex items-center rounded-full border bg-white px-3 py-1 kc-type-caption font-medium"
                            style={{ borderColor: KC_COLORS.border, color: KC_COLORS.navy700 }}
                          >
                            {part.chapterCount} chapters
                          </span>
                        </div>
                      </div>

                      <ol className="m-0 p-0 list-none divide-y" style={{ borderColor: KC_COLORS.border }}>
                        {(part.chapters || []).map((chapter, chapterIndex) => (
                          <li key={chapter.slug} style={{ borderColor: KC_COLORS.border }}>
                            <Link
                              to={ebookChapterPath(page.slug, chapter.slug)}
                              className="group flex items-start sm:items-center gap-3 sm:gap-4 px-4 py-4 sm:px-5 md:px-6 no-underline transition-colors hover:bg-[#F7FAFB]"
                            >
                              <span
                                className="inline-flex h-10 min-w-10 items-center justify-center rounded-xl font-bold shrink-0 mt-0.5 sm:mt-0"
                                style={{
                                  backgroundColor:
                                    chapterIndex === 0
                                      ? 'rgba(0,155,170,0.12)'
                                      : KC_COLORS.lightGrey,
                                  color:
                                    chapterIndex === 0 ? KC_COLORS.teal : KC_COLORS.navy700,
                                  fontSize: '13px',
                                }}
                              >
                                {String(chapter.chapterNumber).padStart(2, '0')}
                              </span>

                              <div className="min-w-0 flex-1">
                                <div
                                  className="font-semibold break-words"
                                  style={{
                                    color: KC_COLORS.primaryNavy,
                                    fontSize: '16px',
                                    lineHeight: '22px',
                                  }}
                                >
                                  {chapter.title}
                                </div>
                                {chapter.subtitle ? (
                                  <p
                                    className="m-0 mt-1 break-words"
                                    style={{
                                      color: KC_COLORS.gray600,
                                      fontSize: '14px',
                                      lineHeight: '20px',
                                    }}
                                  >
                                    {chapter.subtitle}
                                  </p>
                                ) : chapter.summary ? (
                                  <p
                                    className="m-0 mt-1 kc-listing-summary"
                                    style={{
                                      color: KC_COLORS.gray600,
                                      fontSize: '14px',
                                      lineHeight: '20px',
                                    }}
                                  >
                                    {truncateAtWord(
                                      chapter.summary,
                                      KC_TRUNCATE.twoLines
                                    )}
                                  </p>
                                ) : null}
                              </div>

                              <div className="hidden sm:flex items-center gap-2 shrink-0 pl-2">
                                <span
                                  className="kc-type-caption"
                                  style={{ color: KC_COLORS.gray600 }}
                                >
                                  {formatReadTime(chapter.readTimeMinutes)}
                                </span>
                                <ArrowForwardRoundedIcon
                                  className="transition-transform group-hover:translate-x-0.5"
                                  sx={{ fontSize: 18, color: KC_COLORS.teal }}
                                />
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ol>
                    </section>
                  ))}

                  <div className="pt-2">
                    <Link
                      to={KC_HOME_PATH}
                      className="inline-flex items-center gap-1.5 font-semibold no-underline"
                      style={{ color: KC_COLORS.teal }}
                    >
                      <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                      Back to Knowledge Center
                    </Link>
                  </div>
                </div>
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    </>
  );
}

export function KnowledgeCenterEbookPage() {
  return <KnowledgeCenterEbook />;
}
